import { Button, Divider, IconButton, Tab, Tabs } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { useMutation, useQuery } from "@tanstack/react-query";
import { GKClientDataGrid, Grid2 } from "components";
import { PlusIcon, StarFillIcon, StarIcon } from "components/Icons";
import dayjs from "dayjs";
import DashboardLayout from "layouts/DashboardLayout";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import axiosInstance from "utils/axiosInstance";
import { extraColumnsField } from "utils/helpers";
import AddSchemeModal from "./AddSchemeModal";

const formatPercent = (value?: number | null) =>
  value === null || value === undefined ? "-" : `${(value * 100).toFixed(2)}%`;

// Expense ratio and exit load already come as percent values
const formatExpense = (value?: number | null) =>
  value === null || value === undefined ? "-" : `${value}%`;

const getFundManager = (fund: any) => {
  if (typeof fund?.lead === "string") return fund.lead;
  if (fund?.lead?.name) return fund.lead.name;
  if (Array.isArray(fund?.managers) && fund.managers.length) {
    return fund.managers
      .map((manager: any) =>
        typeof manager === "string" ? manager : manager?.name
      )
      .filter(Boolean)
      .join(", ");
  }
  return "-";
};

// Light segmented tabs: grey track, selected tab raised as a white card
const segmentedTabsSx = {
  display: "inline-flex",
  maxWidth: "100%",
  minHeight: 0,
  p: "4px",
  borderRadius: "10px",
  background: "#F5F7FA",
  "& .MuiTabs-indicator": { display: "none" },
  "& .MuiTabs-flexContainer": { gap: "4px" },
  "& .MuiButtonBase-root.MuiTab-root": {
    minHeight: 0,
    padding: "7px 14px",
    borderRadius: "8px",
    fontSize: 14,
    fontWeight: 500,
    color: "var(--neutral-500)",
    transition: "background .15s, color .15s",
  },
  "& .MuiButtonBase-root.MuiTab-root.Mui-selected": {
    background: "var(--white)",
    color: "var(--neutral-900)",
    fontWeight: 600,
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.12)",
  },
};

const MF = () => {
  const [selectedPeriod, setSelectedPeriod] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [openAddScheme, setOpenAddScheme] = useState(false);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["mf-funds-by-category", selectedPeriod],
    queryFn: () =>
      axiosInstance.get(`/mf-screener/funds`, {
        params: {
          groupBy: "category",
          top: 5,
          ...(selectedPeriod && { period: selectedPeriod }),
        },
      }),
    keepPreviousData: true,
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Something went wrong");
    },
  });

  // Latest period first (period is "YYYY-MM", so string compare sorts it)
  const availablePeriods: any[] = [
    ...(data?.data?.meta?.availablePeriods || []),
  ].sort((a, b) => String(b.period).localeCompare(String(a.period)));
  const latestPeriod = availablePeriods[0]?.period;
  const responsePeriod = data?.data?.meta?.period;
  const activePeriod = selectedPeriod ?? latestPeriod ?? false;

  // Open the latest period by default, refetching if the API defaulted to another one
  useEffect(() => {
    if (!selectedPeriod && latestPeriod && latestPeriod !== responsePeriod) {
      setSelectedPeriod(latestPeriod);
    }
  }, [selectedPeriod, latestPeriod, responsePeriod]);

  const { data: favouritesData, refetch: refetchFavourites } = useQuery({
    queryKey: ["mf-favourites"],
    queryFn: () => axiosInstance.get(`/mf-screener/favourites`),
  });

  // Accept either a list of gcodes or a list of fund objects
  const favouriteList: any[] =
    favouritesData?.data?.data || favouritesData?.data?.favourites || [];
  const favouriteCodes = new Set(
    favouriteList.map((item) =>
      String(typeof item === "object" ? item?.gcode : item)
    )
  );

  // Star adds the scheme to favourites, unstar removes it
  const { mutate: toggleFavourite, isLoading: isTogglingFavourite } =
    useMutation({
      mutationFn: ({
        gcode,
        isFavourite,
      }: {
        gcode: string;
        isFavourite: boolean;
      }) =>
        isFavourite
          ? axiosInstance.delete(`/mf-screener/favourites/${gcode}`)
          : axiosInstance.post(`/mf-screener/favourites`, { gcode }),
      onSuccess: (response) => {
        refetchFavourites();
        toast.success(response?.data?.message || "Favourites updated");
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.message || "Something went wrong");
      },
    });

  const categories: any[] = data?.data?.categories || [];
  // Keep the selected category across period switches; fall back to the first one
  const activeCategory =
    categories.find((item) => item.category === selectedCategory)?.category ??
    categories[0]?.category ??
    false;

  const rows =
    categories.find((item) => item.category === activeCategory)?.funds || [];

  const xirrColumn = (label: string, key: string): GridColDef => ({
    ...extraColumnsField(label, `xirr-${key}`, 110, 0),
    valueGetter: (row) => formatPercent(row?.row?.returns?.fund?.[key]),
  });

  const columns: GridColDef[] = [
    {
      field: "favourite",
      headerName: "",
      width: 56,
      sortable: false,
      disableColumnMenu: true,
      align: "center",
      renderCell: (params) => {
        const gcode = params?.row?.gcode;
        const isFavourite = favouriteCodes.has(String(gcode));
        return (
          <IconButton
            size="small"
            disabled={isTogglingFavourite || !gcode}
            onClick={(event) => {
              event.stopPropagation();
              toggleFavourite({ gcode, isFavourite });
            }}
          >
            {isFavourite ? (
              <StarFillIcon width={18} height={18} />
            ) : (
              <StarIcon width={18} height={18} />
            )}
          </IconButton>
        );
      },
    },
    {
      ...extraColumnsField("Scheme Name", "fundName", 250, 2),
    },
    {
      ...extraColumnsField("Fund Manager", "lead", 150, 1),
      valueGetter: (row) => getFundManager(row?.row),
    },
    {
      ...extraColumnsField("Benchmark", "benchmark", 180, 1),
      valueGetter: (row) => row?.row?.benchmark?.name || "-",
    },
    xirrColumn("XIRR 1Y", "1y"),
    xirrColumn("XIRR 3Y", "3y"),
    xirrColumn("XIRR 5Y", "5y"),
    xirrColumn("XIRR SI", "sinceInception"),
    {
      ...extraColumnsField("Expense (Direct)", "expenseDirect", 120, 0),
      valueGetter: (row) => formatExpense(row?.row?.expense?.direct?.value),
    },
    {
      ...extraColumnsField("Expense (Regular)", "expenseRegular", 120, 0),
      valueGetter: (row) => formatExpense(row?.row?.expense?.regular?.value),
    },
    {
      ...extraColumnsField("Exit Load %", "exitLoadPercent", 100, 0),
      valueGetter: (row) => formatExpense(row?.row?.exitLoad?.percent),
    },
    {
      ...extraColumnsField("Exit Load Days", "exitLoadDays", 110, 0),
      valueGetter: (row) => row?.row?.exitLoad?.days ?? "-",
    },
  ];

  return (
    <DashboardLayout title="MF">
      <Grid2 container spacing={1}>
        <Grid2
          size={12}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={2}
        >
          <Tabs
            variant="scrollable"
            value={activePeriod}
            onChange={(_, value) => setSelectedPeriod(value)}
            sx={segmentedTabsSx}
          >
            {availablePeriods.map((item) => (
              <Tab
                key={item.period}
                disableRipple
                label={dayjs(`${item.period}-01`).format("MMM YYYY")}
                value={item.period}
              />
            ))}
          </Tabs>
          <Button
            variant="contained"
            startIcon={<PlusIcon />}
            onClick={() => setOpenAddScheme(true)}
            sx={{ flexShrink: 0 }}
          >
            Add Scheme
          </Button>
        </Grid2>
        <Grid2 size={12}>
          <Tabs
            variant="scrollable"
            value={activeCategory}
            onChange={(_, value) => setSelectedCategory(value)}
            // Keep the arrow visible but greyed out when it can't scroll
            sx={{ "& .MuiTabs-scrollButtons.Mui-disabled": { opacity: 0.3 } }}
          >
            {categories.map((item) => (
              <Tab
                key={item.category}
                disableRipple
                label={item.category}
                value={item.category}
              />
            ))}
          </Tabs>
          <Divider />
        </Grid2>
        <Grid2 size={12}>
          <GKClientDataGrid
            columns={columns}
            rows={rows}
            getRowId={(row) => `${row.category}-${row.schemeCode}`}
            loading={isLoading || isFetching}
            showFiltersButton={false}
          />
        </Grid2>
      </Grid2>

      {openAddScheme && (
        <AddSchemeModal
          open={openAddScheme}
          setOpen={setOpenAddScheme}
          period={activePeriod || undefined}
        />
      )}
    </DashboardLayout>
  );
};

export default MF;
